import { EditorRoleEnum } from './EditorRole'
import { z } from 'zod'
import { Workdays } from './Workdays'
import { NoticePeriodUnitEnum, NoticePeriodReferenceEnum } from './NoticePeriod'
import { DIFF_IGNORE } from '../diffConstants'

export const ContractDetails = z
  .object({
    isBodyLeased: z.boolean(),
    isMandatoryInternship: z.boolean().nullable().optional(),
    internshipType: z.string().nullable().optional(),
    /**
     * The employer's answer to "has working-student status?" for a pre-,
     * during- or post-study intern. Ops reads it to decide by hand whether
     * the contract should be Personengruppe 106 instead of 105; nothing
     * switches automatically.
     */
    isWorkingStudent: z.boolean().nullable().optional(),
    isPensionInsuranceExempt: z.boolean().nullable().optional(),
    isPassOnTax: z.boolean().nullable().optional(),
    isShareholder: z.boolean().nullable().optional(),
    isMarginalEmployment: z.boolean().nullable().optional(),
    isSocialSecurityContributor: z.boolean().nullable(),
    jobTitle: z.string(),
    costCenter: z.string().nullable().default(null),
    jobType: z.string(),
    /**
     * Two meanings, depending on the import that set it: a four-digit
     * Beitragsgruppenschlüssel from a DATEV import, or Agenda's five-digit
     * Tätigkeitsschlüssel from an Agenda import. A code set by hand lives in
     * `activityKey`, an override of the BGRS in `bgrsOverride`.
     */
    jobKey: z.string().nullable(),
    /**
     * The official Berufsbenennung of the work the person does, as the
     * Bundesagentur für Arbeit registers it ("Bürofachkraft"). Distinct from
     * `jobTitle`, which is what the employer calls the role ("Bürokraft
     * Zentrale Neuss"); DATEV holds both, and only this one resolves to a
     * Tätigkeitsschlüssel.
     */
    activityDesignation: z.string().nullable().optional(),
    /**
     * Positions 1-5 of the Tätigkeitsschlüssel, set by hand when the
     * designation resolves to nothing. Wins over `activityDesignation`.
     */
    activityKey: z
      .string()
      .regex(/^\d{5}$/)
      .nullable()
      .optional(),
    /**
     * A four-digit Beitragsgruppenschlüssel an operator set by hand when the
     * automatic classification could not resolve one. Read ahead of derivation,
     * so it is an explicit escape hatch, not a silent default.
     */
    bgrsOverride: z
      .string()
      .regex(/^\d{4}$/)
      .nullable()
      .default(null),
    socialSecurityCheck: z.boolean().nullable(),
    /**
     * For minijobs (jobType 109): Controls the taxation method.
     * - true = Individual taxation (Steuerklasse) - employee selects their tax bracket
     * - false/null/undefined = Pauschalsteuer (2% flat rate, default) - tax bracket is 'zero'
     */
    usesIndividualTaxation: z.boolean().nullable().optional(),
  })
  .strict()

export type ContractDetails = z.infer<typeof ContractDetails>

export const ContractDates = z
  .object({
    startDate: z.string().date(),
    /**
     * The end date of the temporary contract. May be null even if the contract is temporary.
     */
    endDate: z.string().date().nullable(),
    /**
     * If true, the contract is a temporary contract. A contract can be temporary but without an end date.
     */
    temporary: z.boolean().nullable(),
    /**
     * The date on which the employee was notified of the termination of the contract.
     */
    terminationNoticeDate: z.string().date().nullable(),
    /**
     * The date on which the contract is terminated.
     */
    terminationDate: z.string().date().nullable(),
    /**
     * Where the terminationDate was derived from during Gastromatic sync.
     * 'payroll' = payroll.terminatedOn (authoritative)
     * 'endDate' = contract.endDate was earliest
     * 'deactivatedAt' = derived from deactivation click (needs operator verification)
     * null = not set by sync (manual entry or non-Gastromatic company)
     */
    terminationDateSource: z
      .enum(['payroll', 'endDate', 'deactivatedAt'])
      .nullable()
      .default(null),
    /**
     * The date on which the trial period ends.
     */
    trialPeriodEndDate: z.string().date().nullable(),
  })
  .strict()

export type ContractDates = z.infer<typeof ContractDates>

export const exitScenarioValues = [
  'employeeTermination',
  'employerTermination',
  'terminationAgreement',
  'fixedTermExpiry',
] as const
export const ExitScenarioEnum = z.enum(exitScenarioValues)
export type ExitScenario = (typeof exitScenarioValues)[number]

export const exitTerminationReasonValues = [
  'operational',
  'behavioral',
  'personal',
] as const
export const ExitTerminationReasonEnum = z.enum(exitTerminationReasonValues)
export type ExitTerminationReason = (typeof exitTerminationReasonValues)[number]

export const exitAgreementInitiativeValues = ['employer', 'employee'] as const
export const ExitAgreementInitiativeEnum = z.enum(exitAgreementInitiativeValues)
export type ExitAgreementInitiative =
  (typeof exitAgreementInitiativeValues)[number]

export const ContractExit = z
  .object({
    scenario: ExitScenarioEnum,
    receivedDate: z.string().date().nullable().default(null),
    receivedInWriting: z.boolean().nullable().default(null),
    employerTerminationReason:
      ExitTerminationReasonEnum.nullable().default(null),
    agreementInitiative: ExitAgreementInitiativeEnum.nullable().default(null),
    agreementSeverance: z.boolean().nullable().default(null),
    agreementSeveranceAmount: z.number().nullable().default(null),
    fixedTermContractDate: z.string().date().nullable().default(null),
    fixedTermExtended: z.boolean().nullable().default(null),
    noticePeriodAccepted: z.boolean().default(false),
    noticePeriodCustom: z.boolean().default(false),
    noticePeriodValue: z.number().nullable().default(null),
    noticePeriodUnit: NoticePeriodUnitEnum.nullable().default(null),
    noticePeriodReference: NoticePeriodReferenceEnum.nullable().default(null),
  })
  .strict()

export type ContractExit = z.infer<typeof ContractExit>

export const contractAdditionalContractComponentCompanyCarDriveType = [
  'combustion',
  'hybrid',
  'electric',
] as const

export const ContractAdditionalContractComponentCompanyCarDriveTypeEnum =
  z.enum(contractAdditionalContractComponentCompanyCarDriveType)

export type ContractAdditionalContractComponentCompanyCarDriveType =
  (typeof contractAdditionalContractComponentCompanyCarDriveType)[number]

export const contractAdditionalContractComponentCompanyCarPrivateUse = [
  'no',
  'flatrate',
  'logbook',
] as const

export const ContractAdditionalContractComponentCompanyCarPrivateUseEnum =
  z.enum(contractAdditionalContractComponentCompanyCarPrivateUse)

export type ContractAdditionalContractComponentCompanyCarPrivateUse =
  (typeof contractAdditionalContractComponentCompanyCarPrivateUse)[number]

/**
 * Marks components created after mandatory field validation was introduced.
 * Legacy components deliberately keep `null`, so enabling the stricter rules
 * cannot turn every pre-existing incomplete record into a validation finding.
 */
export const additionalContractComponentRequiredFieldsVersion = 1

const AdditionalContractComponentRequiredFieldsVersion = z
  .number()
  .int()
  .min(1)
  .nullable()
  .default(null)

export const ContractAdditionalContractComponentCompanyCar = z
  .object({
    type: z.literal('companyCar'),
    requiredFieldsVersion: AdditionalContractComponentRequiredFieldsVersion,
    // price/driveType are nullable so an imported car with incomplete source
    // data (e.g. a missing Bemessungsgrundlage) is kept and surfaced as an
    // incomplete component via form validation, instead of being dropped.
    price: z.number().nullable(),
    driveType:
      ContractAdditionalContractComponentCompanyCarDriveTypeEnum.nullable(),
    acquisitionDate: z.string().date().nullable().optional(),
    privateUse:
      ContractAdditionalContractComponentCompanyCarPrivateUseEnum.nullable().optional(),
    commute: z.boolean().nullable().optional(),
    // Stable identifier used to pair this car with its commute component
    // (`commute.carId`). Absent on records created before the link existed;
    // those still fall back to the positional pairing.
    id: z.string().nullable().optional(),
    // Kennzeichen. Human-visible label, also the source of the imported `id`.
    plate: z.string().nullable().optional(),
    /**
     * Nutzungsende: when the car went back. A car that leaves the fleet is
     * closed rather than deleted, so what was taxed while it was driven stays
     * on the contract.
     */
    usageEndDate: z.string().date().nullable().optional(),
    // Whether the employee pays towards the car at all. Kept separate from the
    // amount so the answer survives before a figure is known, the same way
    // `commute` gates the Entfernungspauschale. Null on records that predate
    // it; those fall back to whether an amount is present.
    coPayment: z.boolean().nullable().optional(),
    // AN-Zuzahlung in EUR per month.
    monthlyCoPayment: z.number().nullable().optional(),
    // Only meaningful for privateUse 'logbook'.
    logbookKmPerMonth: z.number().nullable().optional(),
    sourceSystem: z.string().optional(),
    sourceId: z.string().optional(),
  })
  .strict()

export type ContractAdditionalContractComponentCompanyCar = z.infer<
  typeof ContractAdditionalContractComponentCompanyCar
>

export const ContractAdditionalContractComponentBicycle = z
  .object({
    type: z.literal('bicycle'),
    price: z.number(),
    transferDate: z.string().date().nullable().optional(),
    employerSubsidy: z.number().nullable().optional(),
    salaryConversion: z.number().nullable().optional(),
    sourceSystem: z.string().optional(),
    sourceId: z.string().optional(),
  })
  .strict()

export type ContractAdditionalContractComponentBicycle = z.infer<
  typeof ContractAdditionalContractComponentBicycle
>

export const contractAdditionalContractComponentCommuteTaxationType = [
  'standard',
  'flatrate',
] as const

export const ContractAdditionalContractComponentCommuteTaxationTypeEnum =
  z.enum(contractAdditionalContractComponentCommuteTaxationType)

export type ContractAdditionalContractComponentCommuteTaxationType =
  (typeof contractAdditionalContractComponentCommuteTaxationType)[number]

export const ContractAdditionalContractComponentCommute = z
  .object({
    type: z.literal('commute'),
    requiredFieldsVersion: AdditionalContractComponentRequiredFieldsVersion,
    taxationType:
      ContractAdditionalContractComponentCommuteTaxationTypeEnum.nullable(),
    distance: z.number().nullable(),
    // References `companyCar.id`. Null on records written before the link
    // existed; pairing then falls back to position/count.
    carId: z.string().nullable().optional(),
    sourceSystem: z.string().optional(),
    sourceId: z.string().optional(),
  })
  .strict()

export type ContractAdditionalContractComponentCommute = z.infer<
  typeof ContractAdditionalContractComponentCommute
>

export const contractAdditionalContractComponentPensionSubsidyType = [
  'additional',
  'swap',
] as const

export const ContractAdditionalContractComponentPensionSubsidyTypeEnum = z.enum(
  contractAdditionalContractComponentPensionSubsidyType
)

export type ContractAdditionalContractComponentPensionSubsidyType =
  (typeof contractAdditionalContractComponentPensionSubsidyType)[number]

export const contractAdditionalContractComponentPensionInsuranceType = [
  'directInsurance',
  'pensionFund',
  'pensionScheme',
  'supportFund',
] as const

export const ContractAdditionalContractComponentPensionInsuranceTypeEnum =
  z.enum(contractAdditionalContractComponentPensionInsuranceType)

export type ContractAdditionalContractComponentPensionInsuranceType =
  (typeof contractAdditionalContractComponentPensionInsuranceType)[number]

export const contractAdditionalContractComponentPensionTaxScheme = [
  'par3nr63',
  'par40b',
] as const

export const ContractAdditionalContractComponentPensionTaxSchemeEnum = z.enum(
  contractAdditionalContractComponentPensionTaxScheme
)

export type ContractAdditionalContractComponentPensionTaxScheme =
  (typeof contractAdditionalContractComponentPensionTaxScheme)[number]

export const contractAdditionalContractComponentPensionPayMethod = [
  'transfer',
  'directDebit',
] as const

export const ContractAdditionalContractComponentPensionPayMethodEnum = z.enum(
  contractAdditionalContractComponentPensionPayMethod
)

export type ContractAdditionalContractComponentPensionPayMethod =
  (typeof contractAdditionalContractComponentPensionPayMethod)[number]

export const contractAdditionalContractComponentPensionSubsidyMode = [
  'manual',
  'mandatory15',
] as const

export const ContractAdditionalContractComponentPensionSubsidyModeEnum = z.enum(
  contractAdditionalContractComponentPensionSubsidyMode
)

export type ContractAdditionalContractComponentPensionSubsidyMode =
  (typeof contractAdditionalContractComponentPensionSubsidyMode)[number]

export const contractAdditionalContractComponentInterval = [
  'monthly',
  'quarterly-jan/apr/jul/oct',
  'quarterly-feb/may/aug/nov',
  'quarterly-mar/jun/sep/dec',
  'semiannually-jan/jun',
  'semiannually-feb/jul',
  'semiannually-mar/aug',
  'semiannually-apr/sep',
  'semiannually-may/oct',
  'semiannually-jun/nov',
  'semiannually-jul/dez',
  'annually-jan',
  'annually-feb',
  'annually-mar',
  'annually-apr',
  'annually-may',
  'annually-jun',
  'annually-jul',
  'annually-aug',
  'annually-sep',
  'annually-oct',
  'annually-nov',
  'annually-dec',
] as const

export const ContractAdditionalContractComponentIntervalEnum = z.enum(
  contractAdditionalContractComponentInterval
)

export type ContractAdditionalContractComponentInterval =
  (typeof contractAdditionalContractComponentInterval)[number]

export const ContractAdditionalContractComponentPension = z
  .object({
    type: z.literal('pension'),
    requiredFieldsVersion: AdditionalContractComponentRequiredFieldsVersion,
    interval:
      ContractAdditionalContractComponentIntervalEnum.nullable().optional(),
    amount: z.number().nullable(),
    employerSubsidy: z.number().nullable(),
    subsidyType:
      ContractAdditionalContractComponentPensionSubsidyTypeEnum.nullable(),
    recipient: z.string().nullable().optional(),
    iban: z.string().nullable().optional(),
    bic: z.string().nullable().optional(),
    bank: z.string().nullable().optional(),
    purpose: z.string().nullable().optional(),
    // Real Vertragsnummer. Preferred merge identity; legacy records only carry
    // the Eigene Vertragsnummer in `purpose` and keep matching on that.
    contractNo: z.string().nullable().optional(),
    insuranceType:
      ContractAdditionalContractComponentPensionInsuranceTypeEnum.nullable().optional(),
    // Derived from the contract, not a manual input.
    taxScheme:
      ContractAdditionalContractComponentPensionTaxSchemeEnum.nullable().optional(),
    contractStart: z.string().date().nullable().optional(),
    contractEnd: z.string().date().nullable().optional(),
    payMethod:
      ContractAdditionalContractComponentPensionPayMethodEnum.nullable().optional(),
    // Verwendungszweck; only meaningful for payMethod 'transfer'.
    paymentReference: z.string().nullable().optional(),
    subsidyMode:
      ContractAdditionalContractComponentPensionSubsidyModeEnum.nullable().optional(),
    /**
     * DATEV LODAS "Eigene Vertragsnummer" (`avs_vertrags_nr#psd`, NUM 1–99):
     * the key a bAV contract is known by inside one employee's master data.
     *
     * Allocated when the contract is written, never at export time — a
     * Firestore write during an export triggers `onWritten` and feeds the
     * changelog that same export reads. It is what makes a second export
     * update the DATEV record the first one created instead of adding another
     * contract beside it, so it is never reused and never renumbered.
     *
     * Optional because the schema is `.strict()` and every component stored
     * before this field existed has to stay valid.
     */
    datevNr: z.number().int().min(1).max(99).nullable().optional(),
    sourceSystem: z.string().optional(),
    sourceId: z.string().optional(),
  })
  .strict()

export type ContractAdditionalContractComponentPension = z.infer<
  typeof ContractAdditionalContractComponentPension
>

export const ContractAdditionalContractComponentNonCashBenefit = z
  .object({
    type: z.literal('nonCashBenefit'),
    name: z.string().nullable().optional(),
    amount: z.number().nullable(),
    sourceSystem: z.string().optional(),
    sourceId: z.string().optional(),
  })
  .strict()

export type ContractAdditionalContractComponentNonCashBenefit = z.infer<
  typeof ContractAdditionalContractComponentNonCashBenefit
>

export const ContractAdditionalContractComponentPublicTransportSubsidy = z
  .object({
    type: z.literal('publicTransportSubsidy'),
    name: z.string().nullable().optional(),
    amount: z.number().nullable(),
    sourceSystem: z.string().optional(),
    sourceId: z.string().optional(),
  })
  .strict()

export type ContractAdditionalContractComponentPublicTransportSubsidy = z.infer<
  typeof ContractAdditionalContractComponentPublicTransportSubsidy
>

export const ContractAdditionalContractComponentPublicTransportTicket = z
  .object({
    type: z.literal('publicTransportTicket'),
    name: z.string().nullable().optional(),
    amount: z.number().nullable(),
    sourceSystem: z.string().optional(),
    sourceId: z.string().optional(),
  })
  .strict()

export type ContractAdditionalContractComponentPublicTransportTicket = z.infer<
  typeof ContractAdditionalContractComponentPublicTransportTicket
>

export const ContractAdditionalContractComponentVWL = z
  .object({
    type: z.literal('vwl'),
    amount: z.number().nullable(),
    employerSubsidy: z.number().nullable(),
    recipient: z.string().nullable().optional(),
    iban: z.string().nullable().optional(),
    bic: z.string().nullable().optional(),
    bank: z.string().nullable().optional(),
    purpose: z.string().nullable().optional(),
    sourceSystem: z.string().optional(),
    sourceId: z.string().optional(),
  })
  .strict()

export type ContractAdditionalContractComponentVWL = z.infer<
  typeof ContractAdditionalContractComponentVWL
>

export const ContractAdditionalContractComponentOther = z
  .object({
    type: z.literal('other'),
    interval:
      ContractAdditionalContractComponentIntervalEnum.nullable().optional(),
    name: z.string().nullable(),
    amount: z.number().nullable(),
    sourceSystem: z.string().optional(),
    sourceId: z.string().optional(),
  })
  .strict()

export type ContractAdditionalContractComponentOther = z.infer<
  typeof ContractAdditionalContractComponentOther
>

export const contractAdditionalContractComponentGarnishmentType = [
  'wageGarnishment',
  'consumerInsolvency',
] as const

export const ContractAdditionalContractComponentGarnishmentTypeEnum = z.enum(
  contractAdditionalContractComponentGarnishmentType
)

export type ContractAdditionalContractComponentGarnishmentType =
  (typeof contractAdditionalContractComponentGarnishmentType)[number]

/**
 * Pfändung / Lohnabtretung.
 *
 * Every field is optional: the block records what the employer knows about a
 * garnishment so it can be handed to payroll. The pfändbares Einkommen itself,
 * the Drittschuldnererklärung and the rank shifting when one garnishment ends
 * are DATEV's job and deliberately out of scope here.
 */
export const ContractAdditionalContractComponentGarnishment = z
  .object({
    type: z.literal('garnishment'),
    requiredFieldsVersion: AdditionalContractComponentRequiredFieldsVersion,
    garnishmentType:
      ContractAdditionalContractComponentGarnishmentTypeEnum.nullable().optional(),
    // Gläubiger
    creditor: z.string().nullable().optional(),
    // Aktenzeichen
    caseReference: z.string().nullable().optional(),
    // Gesamtforderung
    totalAmount: z.number().nullable().optional(),
    /**
     * Whether a fixed monthly rate was agreed instead of the rate the
     * exemption tables give. Almost none are, so the amount is only asked for
     * — and only meaningful — when this says yes.
     */
    fixedRate: z.boolean().nullable().optional(),
    // Feste monatliche Rate
    monthlyRate: z.number().nullable().optional(),
    // Unterhaltsberechtigte Personen
    dependents: z.number().nullable().optional(),
    // Rangfolge
    priority: z.number().nullable().optional(),
    validFrom: z.string().date().nullable().optional(),
    validTo: z.string().date().nullable().optional(),
    iban: z.string().nullable().optional(),
    // Left empty for a German IBAN, where the bank derives both itself. Same
    // handling as the employee's own banking details.
    bic: z.string().nullable().optional(),
    bank: z.string().nullable().optional(),
    // Verwendungszweck
    paymentReference: z.string().nullable().optional(),
    /**
     * Pfändungsbeschluss-Nr. for the DATEV LODAS export — see
     * `lodasGarnishmentNumbers.ts`. Allocated on write and then fixed: it is
     * the identity of the record in LODAS, so it must not move.
     */
    datevNr: z.number().int().min(1).max(99).nullable().optional(),
    sourceSystem: z.string().optional(),
    sourceId: z.string().optional(),
  })
  .strict()

export type ContractAdditionalContractComponentGarnishment = z.infer<
  typeof ContractAdditionalContractComponentGarnishment
>

export const ContractAdditionalContractComponent = z.discriminatedUnion(
  'type',
  [
    ContractAdditionalContractComponentCompanyCar,
    ContractAdditionalContractComponentBicycle,
    ContractAdditionalContractComponentCommute,
    ContractAdditionalContractComponentPension,
    ContractAdditionalContractComponentNonCashBenefit,
    ContractAdditionalContractComponentPublicTransportSubsidy,
    ContractAdditionalContractComponentPublicTransportTicket,
    ContractAdditionalContractComponentVWL,
    ContractAdditionalContractComponentGarnishment,
    ContractAdditionalContractComponentOther,
  ]
)

export type ContractAdditionalContractComponent = z.infer<
  typeof ContractAdditionalContractComponent
>

export const contractRemunerationType = ['fixed', 'hourly'] as const

export const ContractRemunerationTypeEnum = z.enum(contractRemunerationType)

export type ContractRemunerationType = (typeof contractRemunerationType)[number]

export const contractRemunerationPaymentType = [
  'wireTransfer',
  'cash',
  'none',
] as const

export const ContractRemunerationPaymentTypeEnum = z.enum(
  contractRemunerationPaymentType
)

export type ContractRemunerationPaymentType =
  (typeof contractRemunerationPaymentType)[number]

export const temporaryAbsenceCompensationReason = [
  'employmentContract',
  'collectiveAgreement',
  'worksAgreement',
] as const

export const TemporaryAbsenceCompensationReasonEnum = z.enum(
  temporaryAbsenceCompensationReason
)

export type TemporaryAbsenceCompensationReason =
  (typeof temporaryAbsenceCompensationReason)[number]

export const ContractRemuneration = z
  .object({
    vacationEntitlementDays: z.number().nullable(),
    temporaryAbsenceCompensation: z.boolean().default(true),
    temporaryAbsenceCompensationReason:
      TemporaryAbsenceCompensationReasonEnum.nullable(),
    additionalContractComponents: z
      .array(ContractAdditionalContractComponent)
      .nullable(),
    regularHours: z.boolean().default(true).nullable(),
    workdays: Workdays.nullable(),
    salary: z.number().nullable(),
    hourly: z.number().nullable(),
    hours: z.number().nullable(),
    paymentType: ContractRemunerationPaymentTypeEnum.nullable(),
    type: ContractRemunerationTypeEnum.nullable(),
  })
  .strict()

export type ContractRemuneration = z.infer<typeof ContractRemuneration>

/**
 * The working time a contract held from `validFrom` until the next version.
 * The latest version always reads the live `remuneration` values instead of
 * its own copy, so a write that bypasses the history acts as a correction.
 */
export const ContractWorkingTimeVersion = z
  .object({
    validFrom: z.string().date(),
    hours: z.number().nullable(),
    regularHours: z.boolean().nullable(),
    workdays: Workdays.nullable(),
    vacationEntitlementDays: z.number().nullable(),
  })
  .strict()

export type ContractWorkingTimeVersion = z.infer<
  typeof ContractWorkingTimeVersion
>

export const ContractEmployer = z
  .object({
    name: z.string(),
    uid: z.string(),
    usesTimeTracking: z.boolean(),
  })
  .strict()

export type ContractEmployer = z.infer<typeof ContractEmployer>

export const ContractEmployee = z
  .object({
    employeeNumber: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    userUid: z.string().nullable(),
    /**
     * Copied onto the contract alongside the name by the importers; stored
     * documents carry the values of the employee `gender` enum. Deliberately
     * a plain string and not that enum: nothing reads this field, no write has
     * ever validated it, and because the schema is `.strict()` an unexpected
     * value would fail the whole document — silently, since the Firestore
     * converter swallows parse errors. Optional so documents written before
     * it existed stay valid and existing object literals keep compiling.
     */
    salutation: z.string().nullable().optional(),
  })
  .strict()

export type ContractEmployee = z.infer<typeof ContractEmployee>

export const Contract = z
  .object({
    active: z.boolean(),
    deleted: z.boolean(),
    /**
     * Unix ms of the most recent not-deleted → deleted transition. Unlike
     * updatedAt it is never moved by later writes that keep the contract
     * deleted; a resurrection leaves it untouched and only a fresh deletion
     * stamps a new value. Null for contracts deleted before the field existed
     * and for contracts that were never deleted.
     */
    deletedAt: z.number().nullable().default(null),
    employee: ContractEmployee,
    employer: ContractEmployer,
    remuneration: ContractRemuneration,
    dates: ContractDates,
    details: ContractDetails,
    exit: ContractExit.nullable().default(null),
    updatedBy: z.string(),
    updatedByName: z.string(),
    updatedByRole: EditorRoleEnum,
    updatedAt: z.number(),
    createdBy: z.string(),
    createdByName: z.string(),
    createdByRole: EditorRoleEnum,
    createdAt: z.number(),
    invitationUid: z.string().nullable(),
    invitationAcceptedByEmail: z.string().nullable(),
    /**
     * Bookkeeping kept beside the values it records, so it is ignored by the
     * changelog: the change that wrote it is already listed there.
     */
    workingTimeHistory: z
      .array(ContractWorkingTimeVersion)
      .default([])
      .describe(DIFF_IGNORE),
  })
  .strict()

export type Contract = z.infer<typeof Contract>
